import type { FundingApplicationSchemas } from "api-types";

import {
  ACCOUNTING_THRESHOLD_BY_CURRENCY,
  CASHFLOW_THRESHOLD_BY_CURRENCY,
  RSS_ACCOUNTING_THRESHOLD_BY_CURRENCY,
} from "../components/constants";
import type { FormattedFundingApplicationScreen } from "../queries/funding-application/queries";
import { UploadDocumentType, type BusinessModelData } from "../types";

import { isEcommerceConnectorSelected, isPaymentsConnectorSelected } from "./connectors";

export const isRssBusinessModel = (businessModel: BusinessModelData) => {
  return businessModel && !["ecommerce_dtc", "wholesale"].includes(businessModel);
};

export const isRevenueRequired = (businessModel: BusinessModelData, data: FormattedFundingApplicationScreen) => {
  // RSS businesses do not require revenue platforms
  if (isRssBusinessModel(businessModel)) {
    return false;
  }
  return !data.hasRetail || data.retailPercentage !== "over_eighty";
};

export const getAccountingThreshold = (businessModel: BusinessModelData) => {
  return isRssBusinessModel(businessModel) ? RSS_ACCOUNTING_THRESHOLD_BY_CURRENCY : ACCOUNTING_THRESHOLD_BY_CURRENCY;
};

type AccountingDataRequirement =
  | "none" // below the lower threshold
  | "optional" // "grey zone"
  | "required"; // above the upper threshold

export const getAccountingDataRequirement = (
  requestedAmount: number,
  currency: string,
  threshold: Record<string, number> // Thresholds for requested amount (by currency)
): AccountingDataRequirement => {
  if (requestedAmount <= threshold[currency] * 0.8) {
    return "none";
  }

  if (requestedAmount > threshold[currency] * 2) {
    return "required";
  }

  return "optional";
};

export const NOT_LISTED = "not_listed";

export const isAccountingRequired = (
  data: FormattedFundingApplicationScreen,
  threshold: Record<string, number>,
  countryOfIncorporation?: string
) => {
  if (countryOfIncorporation === "DE") {
    return false;
  }

  if (data.isWholesale) {
    return true;
  }
  if (data?.requestedAmount && data?.requestedAmountCurrency) {
    if (getAccountingDataRequirement(data.requestedAmount, data.requestedAmountCurrency, threshold) !== "none") {
      return true;
    }
  }

  return false;
};

export const isDebtAgreementRequired = (data: FormattedFundingApplicationScreen, businessModel: BusinessModelData) => {
  if (isRssBusinessModel(businessModel)) {
    return false;
  }
  return data.isWholesale;
};

export const isCashflowRequired = (data: FormattedFundingApplicationScreen, businessModel: BusinessModelData) => {
  if (isRssBusinessModel(businessModel)) {
    return false;
  }
  if (data.isWholesale && data?.requestedAmount && data?.requestedAmountCurrency) {
    if (data?.requestedAmount > CASHFLOW_THRESHOLD_BY_CURRENCY[data?.requestedAmountCurrency]) {
      return true;
    }
  }

  return false;
};

export const isSusaRequired = (
  countryOfIncorporation?: string,
  fundingApplication?: FormattedFundingApplicationScreen
): boolean => {
  return (
    countryOfIncorporation === "DE" &&
    !!fundingApplication &&
    !!fundingApplication.requestedAmount &&
    fundingApplication.requestedAmount > 250000
  );
};

export const isPoolBancarioRequired = (countryOfIncorporation?: string) => {
  return countryOfIncorporation === "ES";
};

/* GA connection only required if no ecommerce or payments connectors selected */
export const isGoogleAnalyticsRequired = (selectedConnectorSlugs: string[]): boolean => {
  return !(isEcommerceConnectorSelected(selectedConnectorSlugs) || isPaymentsConnectorSelected(selectedConnectorSlugs));
};

/* If we allow GA fallback, GA connection only required if no ecommerce or payments connectors selected */
export const isGoogleAnalyticsRequiredNext = (selectedConnectorSlugs: string[], allowGaFallback: boolean): boolean => {
  return (
    allowGaFallback &&
    !(isEcommerceConnectorSelected(selectedConnectorSlugs) || isPaymentsConnectorSelected(selectedConnectorSlugs))
  );
};

export const isAccountingUploadRequired = (
  businessModel: BusinessModelData,
  fundingApplication?: FormattedFundingApplicationScreen,
  countryOfIncorporation?: string
) => {
  if (
    !fundingApplication ||
    !isAccountingRequired(fundingApplication, getAccountingThreshold(businessModel), countryOfIncorporation)
  ) {
    return false;
  }

  if (fundingApplication.accountingDocumentsRequired) {
    return true;
  }

  return !fundingApplication.selectedConnectorSlugs.some((slug) =>
    ["xero", "netsuite", "quickbooks", "codat_accounting"].includes(slug)
  );
};

export const isDocumentOptional = (
  documentType: UploadDocumentType,
  fundingApplication: FormattedFundingApplicationScreen
) => {
  switch (documentType) {
    case UploadDocumentType.CASH_FLOW_FORECAST:
      return fundingApplication.retailPercentage !== "over_eighty";
    case UploadDocumentType.COMPANY_DEBT_AGREEMENT:
      return true;
    case UploadDocumentType.MANAGEMENT_ACCOUNTS:
    case UploadDocumentType.PROFIT_AND_LOSS:
    case UploadDocumentType.BALANCE_SHEET:
      return false;
    case UploadDocumentType.POOL_BANCARIO:
      return true;
    case UploadDocumentType.SUSA:
      return false;
  }
};

export const isDocumentOptionalNext = (
  documentType: FundingApplicationSchemas.DocumentType,
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen
): boolean => {
  if (documentType === "profit-and-loss" || documentType === "balance-sheet") {
    return !!fundingApplicationStages.accounting?.canOptOut;
  }
  const stage = fundingApplicationStages?.documents?.find((stage) => stage.documentType === documentType);
  return !!stage?.canOptOut;
};
